import ApplicationController from "./application_controller";

export default class extends ApplicationController {
  static targets = ["zone", "input", "value", "documentType", "category", "modal", "modalSelect", "modalFilename"]
  static values = { applyId: Number, debug: Boolean }

  connect() {
    this.maxSizeInBytes = 5 * 1024 * 1024; // 5 MB
    this.acceptedTypes = ["application/pdf", "image/jpeg", "image/png"]; // PDF und Bilder
    this.uploading = false;
    this.lastFileName = null;
    this.debug = this._resolveDebugFlag();
    this._installGlobalErrorLogging();
    this._bindFormAndTurboHandlers();
    this._log("connect", {
      applyId: this.hasApplyIdValue ? this.applyIdValue : null,
      ua: navigator.userAgent,
      turboPresent: typeof window.Turbo !== "undefined",
      hasInput: this.hasInputTarget,
      hasCategory: this.hasCategoryTarget
    });
  }

  disconnect() {
    this._removeFormAndTurboHandlers();
    this._log("disconnect");
  }

  openPicker(event) {
    // Kein preventDefault, damit das native Label-Fallback (ohne JS) weiterhin greift
    event.stopPropagation(); // verhindert ggf. doppeltes Öffnen (Bubbling zur Zone)
    if (this.hasInputTarget) this.inputTarget.click();
    this._log("openPicker: clicked input");
  }

  dragOver(event) {
    event.preventDefault();
    if (this.hasZoneTarget) {
      this.zoneTarget.classList.add("ring-2", "ring-purple-400", "bg-purple-50");
    }
    this._log("dragOver");
  }

  dragLeave(event) {
    event.preventDefault();
    if (this.hasZoneTarget) {
      this.zoneTarget.classList.remove("ring-2", "ring-purple-400", "bg-purple-50");
    }
    this._log("dragLeave");
  }

  drop(event) {
    event.preventDefault();
    if (!this.hasInputTarget) return;
    const files = event.dataTransfer.files;
    if (!files || files.length === 0) return;
    this._log("drop", { count: files.length, names: Array.from(files).map(f => f.name) });
    this.assignFiles(files);
  }

  onInputChange() {
    if (!this.hasInputTarget) return;
    this._log("onInputChange", {
      count: this.inputTarget.files ? this.inputTarget.files.length : 0,
      name: this.inputTarget.files && this.inputTarget.files[0] ? this.inputTarget.files[0].name : null
    });
    this.assignFiles(this.inputTarget.files);
  }

  assignFiles(files) {
    if (!files || files.length === 0) return;
    const file = files[0];

    // Mögliche Auto-Vorauswahl je nach Dateityp
    this.autoSelectCategoryByTypeOrName(file);

    // Validierung abhängig von Kategorie
    const beforeValidationCategory = this.hasCategoryTarget ? (this.categoryTarget.selectedOptions[0]?.textContent || "").trim() : null;
    this._log("assignFiles: before validation", {
      file: { name: file.name, type: file.type, size: file.size },
      categoryText: beforeValidationCategory
    });
    if (!this.isTypeAllowedForSelectedCategory(file)) {
      this._warn("assignFiles: validation failed", { file: { name: file.name, type: file.type, size: file.size } });
      return;
    }
    if (file.size > this.maxSizeInBytes) {
      alert("Die Datei darf nicht größer als 5 MB sein.");
      this._warn("assignFiles: file too large", { size: file.size, max: this.maxSizeInBytes });
      this.clearSelection();
      return;
    }

    // Setze Dateien ins Input (für Formular-Submit). input.files ist read-only – nur per DataTransfer setzbar (funktioniert in allen Browsern/Staging).
    this._setInputFiles(files);

    // Anzeige Dateiname
    if (this.hasValueTarget) {
      this.valueTarget.textContent = file.name;
      this.valueTarget.classList.remove("text-gray-400");
      this.valueTarget.classList.add("text-gray-800");
    }
    this.lastFileName = file.name;

    // Dokumenten-Kategorie anzeigen und evtl. vorauswählen
    if (this.hasDocumentTypeTarget) {
      this.documentTypeTarget.classList.remove("hidden");
    }
    this.autoSelectCategoryFromFilename(file.name);

    // Falls noch keine Kategorie gewählt → abbrechen und Hinweis
    const categoryId = this.hasCategoryTarget ? this.categoryTarget.value : null;
    if (!categoryId) {
      // Modal für Kategoriewahl öffnen
      this._log("assignFiles: no category yet → opening modal");
      this.openCategoryModal();
      return;
    }

    // Auto-Upload: gesamtes Formular absenden (wie Klick auf „Hochladen“)
    this._log("assignFiles: auto-uploading via submitParentForm");
    this.submitParentForm();

    // Optik nach Drop zurücksetzen
    if (this.hasZoneTarget) {
      this.zoneTarget.classList.remove("ring-2", "ring-purple-400", "bg-purple-50");
    }
    this._log("assignFiles: finished");
  }

  openCategoryModal() {
    if (!this.hasModalTarget) {
      // Fallback: Fokus auf vorhandene Auswahl
      if (this.hasCategoryTarget) this.categoryTarget.focus();
      this._log("openCategoryModal: no modal target, focusing category");
      return;
    }
    this.modalTarget.classList.remove("hidden");
    // Vorbelegen: falls main select schon einen Wert hat, in Modal übernehmen
    if (this.hasModalSelectTarget && this.hasCategoryTarget && this.categoryTarget.value) {
      this.modalSelectTarget.value = this.categoryTarget.value;
    }
    // Fokus setzen
    if (this.hasModalSelectTarget) this.modalSelectTarget.focus();
    // Dateiname anzeigen
    if (this.hasModalFilenameTarget) {
      const currentName = this.lastFileName || (this.hasInputTarget && this.inputTarget.files && this.inputTarget.files[0] && this.inputTarget.files[0].name) || "";
      this.modalFilenameTarget.textContent = currentName;
    }
    this._log("openCategoryModal: opened", {
      preselected: this.hasModalSelectTarget ? this.modalSelectTarget.value : null,
      filename: this.lastFileName
    });
  }

  closeCategoryModal(event) {
    if (event) event.preventDefault();
    if (this.hasModalTarget) this.modalTarget.classList.add("hidden");
    this._log("closeCategoryModal");
  }

  confirmCategoryModal(event) {
    if (event) event.preventDefault();
    if (this.hasModalSelectTarget && this.hasCategoryTarget) {
      this.categoryTarget.value = this.modalSelectTarget.value;
      const evt = new Event("change", { bubbles: true });
      this.categoryTarget.dispatchEvent(evt);
    }
    this._log("confirmCategoryModal", { selected: this.hasCategoryTarget ? this.categoryTarget.value : null });
    this.closeCategoryModal();
  }

  stopPropagation(event) {
    event.stopPropagation();
    this._log("stopPropagation");
  }

  autoSelectCategoryFromFilename(filename) {
    if (!this.hasCategoryTarget) return;
    const name = (filename || "").toLowerCase();
    const shouldSelectCv =
      name.includes("lebenslauf") || name.includes("cv");
    if (!shouldSelectCv) return;

    // Suche Option, deren Text "Lebenslauf" oder "CV" enthält
    const options = Array.from(this.categoryTarget.options || []);
    const match = options.find(opt => {
      const text = (opt.textContent || "").toLowerCase();
      return text.includes("lebenslauf") || text.includes("cv");
    });
    if (match) {
      this.categoryTarget.value = match.value;
      const evt = new Event("change", { bubbles: true });
      this.categoryTarget.dispatchEvent(evt);
      this._log("autoSelectCategoryFromFilename: selected", { value: match.value, text: match.textContent });
    }
  }

  autoSelectCategoryByTypeOrName(file) {
    if (!this.hasCategoryTarget) return;
    const name = (file.name || "").toLowerCase();
    const isImage = file.type === "image/jpeg" || file.type === "image/png" || name.endsWith(".jpg") || name.endsWith(".jpeg") || name.endsWith(".png");
    if (!isImage) return;
    const options = Array.from(this.categoryTarget.options || []);
    const photoMatch = options.find(opt => {
      const text = (opt.textContent || "").toLowerCase();
      return text.includes("foto") || text.includes("photo") || text.includes("bild");
    });
    if (photoMatch) {
      this.categoryTarget.value = photoMatch.value;
      const evt = new Event("change", { bubbles: true });
      this.categoryTarget.dispatchEvent(evt);
      this._log("autoSelectCategoryByTypeOrName: selected photo category", { value: photoMatch.value, text: photoMatch.textContent });
    }
  }

  isTypeAllowedForSelectedCategory(file) {
    const name = (file.name || "").toLowerCase();
    const isPdf = file.type === "application/pdf" || name.endsWith(".pdf");
    const isImage = file.type === "image/jpeg" || file.type === "image/png" || name.endsWith(".jpg") || name.endsWith(".jpeg") || name.endsWith(".png");

    // Wenn keine Kategorie gewählt ist, erlauben wir beide Typen temporär (Modal folgt)
    const selectedText = this.hasCategoryTarget ? (this.categoryTarget.selectedOptions[0]?.textContent || "").toLowerCase() : "";
    const isPhotoCategory = selectedText.includes("foto") || selectedText.includes("photo") || selectedText.includes("bild");

    if (isPhotoCategory) {
      if (!isImage) {
        alert("Bitte laden Sie ein Bild (JPG oder PNG) hoch.");
        this._warn("isTypeAllowedForSelectedCategory: expected image for photo category", { fileType: file.type, fileName: file.name, categoryText: selectedText });
        this.clearSelection();
        return false;
      }
      this._log("isTypeAllowedForSelectedCategory: OK image for photo category");
      return true;
    } else {
      // Standard: PDF
      if (!isPdf) {
        alert("Bitte laden Sie eine PDF-Datei hoch.");
        this._warn("isTypeAllowedForSelectedCategory: expected PDF for non-photo category", { fileType: file.type, fileName: file.name, categoryText: selectedText });
        this.clearSelection();
        return false;
      }
      this._log("isTypeAllowedForSelectedCategory: OK pdf for non-photo category");
      return true;
    }
  }

  submitParentForm() {
    if (this.uploading) return;
    const form = this.element.closest("form");
    if (!form) return;
    this._setUploading(true);
    this.uploading = true;
    // commit=save (wie der frühere Upload-Button)
    const hiddenCommit = document.createElement("input");
    hiddenCommit.type = "hidden";
    hiddenCommit.name = "commit";
    hiddenCommit.value = "save";
    form.appendChild(hiddenCommit);
    this._log("submitParentForm: submitting form", this._describeForm(form));
    // FormData-Inhalt (ohne Binärdaten) grob inspizieren
    try {
      const fd = new FormData(form);
      const snapshot = [];
      for (const [k, v] of fd.entries()) {
        if (v instanceof File) {
          snapshot.push({ key: k, file: { name: v.name, type: v.type, size: v.size } });
        } else {
          snapshot.push({ key: k, value: String(v).slice(0, 200) });
        }
      }
      this._log("submitParentForm: formdata snapshot", snapshot);
    } catch (e) {
      this._warn("submitParentForm: could not snapshot FormData", { error: String(e) });
    }
    // Absenden (Turbo übernimmt Reload/Navigation)
    if (typeof form.requestSubmit === "function") {
      form.requestSubmit();
    } else {
      form.submit();
    }
  }

  onCategoryChange() {
    // Wenn bereits eine Datei gewählt ist und nun die Kategorie gesetzt/ geändert wurde → sofort hochladen
    const hasFile = this.hasInputTarget && this.inputTarget.files && this.inputTarget.files.length > 0;
    const categoryId = this.hasCategoryTarget ? this.categoryTarget.value : null;
    this._log("onCategoryChange", {
      hasFile,
      categoryId,
      categoryText: this.hasCategoryTarget ? (this.categoryTarget.selectedOptions[0]?.textContent || "").trim() : null
    });
    if (hasFile && categoryId) {
      this.submitParentForm();
    }
  }

  clearSelection() {
    if (this.hasInputTarget) {
      this.inputTarget.value = "";
    }
    if (this.hasValueTarget) {
      this.valueTarget.textContent = "Keine Datei ausgewählt";
      this.valueTarget.classList.remove("text-gray-800");
      this.valueTarget.classList.add("text-gray-400");
    }
    if (this.hasZoneTarget) {
      this.zoneTarget.classList.remove("ring-2", "ring-purple-400", "bg-purple-50");
    }
    this._log("clearSelection");
  }

  _setInputFiles(files) {
    if (!this.hasInputTarget || !files || files.length === 0) return;
    try {
      const dt = new DataTransfer();
      for (let i = 0; i < files.length; i++) dt.items.add(files[i]);
      this.inputTarget.files = dt.files;
    } catch (e) {
      this._warn("_setInputFiles failed", { error: String(e) });
    }
  }

  _setUploading(isUploading) {
    if (!this.hasZoneTarget) return;
    this.zoneTarget.classList.toggle("opacity-60", isUploading);
    this.zoneTarget.classList.toggle("pointer-events-none", isUploading);
    if (this.hasValueTarget) {
      this.valueTarget.textContent = isUploading ? "Wird hochgeladen ..." : this.valueTarget.textContent;
    }
    this._log("_setUploading", { isUploading });
  }

  // ===== Debug- und Event-Hilfsmethoden =====
  _resolveDebugFlag() {
    try {
      if (this.hasDebugValue && this.debugValue === true) return true;
      if (typeof window !== "undefined" && window.DEBUG_ATTACHMENTS === true) return true;
      if (typeof window !== "undefined" && window.localStorage && window.localStorage.getItem("attachments.debug") === "1") return true;
      if (typeof window !== "undefined" && window.location && window.location.search && window.location.search.includes("debug_upload=1")) return true;
      if (typeof window !== "undefined" && window.location && /staging|preview|heroku|railway|vercel/.test(window.location.hostname)) return true;
    } catch (_) { /* noop */ }
    return false;
  }

  _log(message, payload) {
    if (!this.debug) return;
    try {
      const prefix = "[AttachmentDropzone]";
      if (payload !== undefined) {
        console.log(prefix, message, payload);
      } else {
        console.log(prefix, message);
      }
    } catch (_) { /* noop */ }
  }

  _warn(message, payload) {
    if (!this.debug) return;
    try {
      const prefix = "[AttachmentDropzone][WARN]";
      if (payload !== undefined) {
        console.warn(prefix, message, payload);
      } else {
        console.warn(prefix, message);
      }
    } catch (_) { /* noop */ }
  }

  _error(message, payload) {
    if (!this.debug) return;
    try {
      const prefix = "[AttachmentDropzone][ERROR]";
      if (payload !== undefined) {
        console.error(prefix, message, payload);
      } else {
        console.error(prefix, message);
      }
    } catch (_) { /* noop */ }
  }

  _installGlobalErrorLogging() {
    if (!this.debug) return;
    // Einmalige Registrierung pro Controller-Instanz
    if (!this._onWindowError) {
      this._onWindowError = (event) => {
        this._error("window.error", {
          message: event?.message,
          filename: event?.filename,
          lineno: event?.lineno,
          colno: event?.colno,
          error: event?.error ? String(event.error) : null
        });
      };
      window.addEventListener("error", this._onWindowError);
    }
    if (!this._onUnhandledRejection) {
      this._onUnhandledRejection = (event) => {
        this._error("window.unhandledrejection", {
          reason: event?.reason ? String(event.reason) : null
        });
      };
      window.addEventListener("unhandledrejection", this._onUnhandledRejection);
    }
  }

  _bindFormAndTurboHandlers() {
    const form = this.element.closest("form");
    this._bound = this._bound || {};
    if (form && !this._bound.submit) {
      this._bound.submit = (e) => {
        this._log("form submit event", this._describeForm(form));
      };
      form.addEventListener("submit", this._bound.submit);
    }
    // Turbo-Events global lauschen, damit wir Status/Response sehen
    if (!this._bound.turboSubmitStart) {
      this._bound.turboSubmitStart = (e) => {
        // e.target ist das Formular, e.detail.formSubmission.request
        this._log("turbo:submit-start", {
          action: e?.target?.action,
          method: e?.target?.method,
          enctype: e?.target?.enctype
        });
      };
      document.addEventListener("turbo:submit-start", this._bound.turboSubmitStart);
    }
    if (!this._bound.turboSubmitEnd) {
      this._bound.turboSubmitEnd = (e) => {
        const fr = e?.detail?.fetchResponse;
        this._log("turbo:submit-end", {
          success: e?.detail?.success,
          status: fr?.statusCode || fr?.response?.status,
          redirected: fr?.redirected,
          location: fr?.location ? String(fr.location) : null
        });
      };
      document.addEventListener("turbo:submit-end", this._bound.turboSubmitEnd);
    }
    if (!this._bound.beforeFetchRequest) {
      this._bound.beforeFetchRequest = (e) => {
        try {
          const headers = {};
          e.detail.fetchOptions?.headers?.forEach?.((v, k) => { headers[k] = v; });
          this._log("turbo:before-fetch-request", {
            url: e?.detail?.url,
            method: e?.detail?.fetchOptions?.method,
            headers
          });
        } catch (_) {
          this._log("turbo:before-fetch-request");
        }
      };
      document.addEventListener("turbo:before-fetch-request", this._bound.beforeFetchRequest);
    }
    if (!this._bound.beforeFetchResponse) {
      this._bound.beforeFetchResponse = (e) => {
        this._log("turbo:before-fetch-response", {
          status: e?.detail?.fetchResponse?.statusCode,
          redirected: e?.detail?.fetchResponse?.redirected
        });
      };
      document.addEventListener("turbo:before-fetch-response", this._bound.beforeFetchResponse);
    }
  }

  _removeFormAndTurboHandlers() {
    const form = this.element.closest("form");
    if (form && this._bound?.submit) form.removeEventListener("submit", this._bound.submit);
    if (this._bound?.turboSubmitStart) document.removeEventListener("turbo:submit-start", this._bound.turboSubmitStart);
    if (this._bound?.turboSubmitEnd) document.removeEventListener("turbo:submit-end", this._bound.turboSubmitEnd);
    if (this._bound?.beforeFetchRequest) document.removeEventListener("turbo:before-fetch-request", this._bound.beforeFetchRequest);
    if (this._bound?.beforeFetchResponse) document.removeEventListener("turbo:before-fetch-response", this._bound.beforeFetchResponse);
    if (this._onWindowError) window.removeEventListener("error", this._onWindowError);
    if (this._onUnhandledRejection) window.removeEventListener("unhandledrejection", this._onUnhandledRejection);
    this._bound = {};
  }

  _describeForm(form) {
    if (!form) return null;
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");
    return {
      action: form.action,
      method: form.method,
      enctype: form.enctype,
      hasCsrfMeta: Boolean(token),
      turbo: form.getAttribute("data-turbo"),
      hasFile: this.hasInputTarget && this.inputTarget.files && this.inputTarget.files.length > 0,
      file: this.hasInputTarget && this.inputTarget.files && this.inputTarget.files[0] ? {
        name: this.inputTarget.files[0].name,
        type: this.inputTarget.files[0].type,
        size: this.inputTarget.files[0].size
      } : null,
      categoryId: this.hasCategoryTarget ? this.categoryTarget.value : null,
      categoryText: this.hasCategoryTarget ? (this.categoryTarget.selectedOptions[0]?.textContent || "").trim() : null
    };
  }
}


